import React, { useEffect, useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Grid,
  Button,
  CssBaseline,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  IconButton,
  Menu,
  MenuItem,
  TextField,
} from "@mui/material";
import { Close, ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import { tokens } from "../../../theme/theme";
import { callApi } from "../../../utils/apiClient";
import { API_ENDPOINTS } from "../../../config/api/apiCall";
import CallAlertBar from "../../../Layout/Alertbar";
import AddUpdateBank from "./AddUpdateBank";
import { useNavigate } from "react-router-dom";
import CustomTableContainer from "../../../components/table/CustomTableContainer";

const BankIndex = ({handleBackWinderMachineEntry}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();

  const [bankList, setBankList] = useState([]);
  const [filteredBankList, setFilteredBankList] = useState([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [bankFormData, setBankFormData] = useState({});

  // Column filters
  const [filters, setFilters] = useState({
    sbank_code: "",
    sbank_name: "",
    sbank_status: "",
  });

  // Menu filter
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuText, setMenuText] = useState("All Banks");

  // Alerts
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertSeverity, setAlertSeverity] = useState("info");
  const [alertMessage, setAlertMessage] = useState("");
  const [alertTitle, setAlertTitle] = useState("");

  // ================= Load Bank List =================
  const LoadBankList = () => {
    callApi(API_ENDPOINTS.MASTER_GET_BANK_LIST, "POST", {
      "Content-Type": "application/json",
    })
      .then((data) => {
        setBankList(data || []);
        setFilteredBankList(data || []);
      })
      .catch(() => {
        setAlertSeverity("error");
        setAlertTitle("Error");
        setAlertMessage("Unable to load bank details");
        setAlertOpen(true);
      });
  };

  useEffect(() => {
    LoadBankList();
  }, []);

  // ================= Menu Filter =================
  const handleMenuClick = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleMenuItemClick = (option) => {
    setMenuText(option);
    handleMenuClose();

    if (option === "All Banks") {
      setFilteredBankList(bankList);
    } else if (option === "Active Banks") {
      setFilteredBankList(
        bankList.filter(
          (x) => x.sbank_status === true || x.sbank_status === "Active"
        )
      );
    } else if (option === "Inactive Banks") {
      setFilteredBankList(
        bankList.filter(
          (x) => x.sbank_status === false || x.sbank_status === "Inactive"
        )
      );
    }
  };

  // ================= Column Filters =================
  useEffect(() => {
    const filtered = bankList.filter((row) => {
      return (
        (row.sbank_code || "")
          .toLowerCase()
          .includes(filters.sbank_code.toLowerCase()) &&
        (row.sbank_name || "")
          .toLowerCase()
          .includes(filters.sbank_name.toLowerCase()) &&
        (row.sbank_status || "")
          .toString()
          .toLowerCase()
          .includes(filters.sbank_status.toLowerCase())
      );
    });

    setFilteredBankList(filtered);
  }, [filters, bankList]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  // ================= Form Handlers =================
  const handleAddBank = () => {
    setBankFormData({});
    setIsFormOpen(true);
  };

  const handleEditBank = (bank) => {
    setBankFormData(bank);
    setIsFormOpen(true);
  };

  const handleBack = () =>  handleBackWinderMachineEntry();

  return (
    <Grid>
      {!isFormOpen && (
        <>
          <CssBaseline />

          <AppBar position="static" sx={{ backgroundColor: "inherit" }}>
            <Toolbar>
              <Typography variant="h4">Bank Master</Typography>

              {/* Menu Filter */}
              <Button
                color="secondary"
                onClick={handleMenuClick}
                endIcon={<ExpandMoreIcon />}
                sx={{ ml: 2, textTransform: "none" }}
              >
                {menuText}
              </Button>

              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
              >
                <MenuItem onClick={() => handleMenuItemClick("All Banks")}>
                  All Banks
                </MenuItem>
                <MenuItem onClick={() => handleMenuItemClick("Active Banks")}>
                  Active Banks
                </MenuItem>
                <MenuItem onClick={() => handleMenuItemClick("Inactive Banks")}>
                  Inactive Banks
                </MenuItem>
              </Menu>

              <Button
                size="small"
                variant="contained"
                color="secondary"
                startIcon={<AccountBalanceIcon />}
                sx={{ ml: "auto", textTransform: "none" }}
                onClick={handleAddBank}
              >
                New Bank
              </Button>

              <IconButton onClick={handleBack}>
                <Close sx={{ fontSize: 16, color: "#2196F3" }} />
              </IconButton>
            </Toolbar>
          </AppBar>

          <CallAlertBar
            parentRollMsgOpen={alertOpen}
            setParentRollMsgOpen={setAlertOpen}
            parentRollSeverityvalue={alertSeverity}
            setParentRollSeverityvalue={setAlertSeverity}
            parentRollAlertTitleValue={alertTitle}
            setParentRollAlertTitleValue={setAlertTitle}
            parentRollMessageValue={alertMessage}
            setParentRollMessageValue={setAlertMessage}
          />

          <Grid container padding={1}>
            <Grid item xs={12}>
              <Box sx={{ maxHeight: "75vh", overflowY: "auto" }}>
                <CustomTableContainer colors={colors}>
                  <TableContainer>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>BANK CODE</TableCell>
                          <TableCell>BANK NAME</TableCell>
                          <TableCell>STATUS</TableCell>
                        </TableRow>

                        {/* Column Filters */}
                        <TableRow>
                          <TableCell>
                            <TextField
                              size="small"
                              fullWidth
                              placeholder="Search Code"
                              value={filters.sbank_code}
                              onChange={(e) =>
                                handleFilterChange(
                                  "sbank_code",
                                  e.target.value
                                )
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              fullWidth
                              placeholder="Search Name"
                              value={filters.sbank_name}
                              onChange={(e) =>
                                handleFilterChange(
                                  "sbank_name",
                                  e.target.value
                                )
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              fullWidth
                              placeholder="Search Status"
                              value={filters.sbank_status}
                              onChange={(e) =>
                                handleFilterChange(
                                  "sbank_status",
                                  e.target.value
                                )
                              }
                            />
                          </TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {filteredBankList.map((bank) => (
                          <TableRow key={bank.sbank_id} hover>
                            <TableCell
                              sx={{
                                color: "teal",
                                textDecoration: "underline",
                                cursor: "pointer",
                              }}
                              onClick={() => handleEditBank(bank)}
                            >
                              {bank.sbank_code}
                            </TableCell>
                            <TableCell>{bank.sbank_name}</TableCell>
                            <TableCell>{bank.sbank_status}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CustomTableContainer>
              </Box>
            </Grid>
          </Grid>
        </>
      )}

      {isFormOpen && (
        <AddUpdateBank
          bankFormData={bankFormData}
          setBankFormData={setBankFormData}
          setIsFormOpen={setIsFormOpen}
          LoadBankList={LoadBankList}
        />
      )}
    </Grid>
  );
};

export default BankIndex;
